import React, { useEffect, useState, useMemo } from "react";
import { api, formatErr } from "../lib/api";
import { Calendar, Building2, Mail, Phone, MapPin, Check, X, Clock, RefreshCw, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Admin queue for lead-generation submissions (demo + signup requests).
 *
 * Backend: GET /api/admin/leads, PATCH /api/admin/leads/{lid}
 *
 * UX:
 *  - 4 status pills (pending / contacted / approved / rejected) with counts
 *  - Optional kind filter (all / demo / signup)
 *  - Card list, expand for full data + admin notes textarea
 *  - Status update via PATCH; on "approved" we keep the row (not auto-creating a
 *    business account here — admin can use the existing "Crea attività" tab)
 */

const STATUS = [
  { v: "pending", label: "In attesa", tone: "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300" },
  { v: "contacted", label: "Contattati", tone: "bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300" },
  { v: "approved", label: "Approvati", tone: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300" },
  { v: "rejected", label: "Rifiutati", tone: "bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300" },
];

const TYPE_LABELS = {
  palestra: "Palestra",
  centro_estetico: "Centro estetico",
  parrucchiere: "Parrucchiere",
  studio_professionale: "Studio professionale",
  altro: "Altro",
};

const SLOT_LABELS = { mattina: "Mattina (9-13)", pomeriggio: "Pomeriggio (14-18)", sera: "Sera (18-20)" };

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
  } catch { return iso; }
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function LeadsAdminPanel() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [kindFilter, setKindFilter] = useState("");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, contacted: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (kindFilter) params.kind = kindFilter;
      const { data } = await api.get("/admin/leads", { params });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, kindFilter]);

  async function updateStatus(lid, newStatus, adminNotes) {
    try {
      await api.patch(`/admin/leads/${lid}`, { status: newStatus, admin_notes: adminNotes || null });
      await load();
    } catch (ex) {
      alert(formatErr(ex.response?.data?.detail) || ex.message);
    }
  }

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="space-y-5" data-testid="leads-admin-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-medium dark:text-zinc-100">Richieste in coda</h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Demo e attivazioni richieste dal sito pubblico. Approva quelle valide e crea poi l'account dalla tab "Crea attività".
          </p>
        </div>
        <button
          onClick={load}
          className="h-9 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-zinc-300"
          data-testid="leads-refresh"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
        </button>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        {STATUS.map((s) => {
          const active = statusFilter === s.v;
          return (
            <button
              key={s.v}
              onClick={() => setStatusFilter(s.v)}
              className={`h-9 px-3.5 rounded-full text-xs font-medium inline-flex items-center gap-2 transition ${
                active ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
              }`}
              data-testid={`leads-filter-${s.v}`}
            >
              {s.label}
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-zinc-200 dark:bg-zinc-700"}`}>
                {counts[s.v] ?? 0}
              </span>
            </button>
          );
        })}
        <div className="h-9 w-px bg-zinc-200 dark:bg-zinc-700 mx-1" />
        <button onClick={() => setKindFilter("")} className={`h-9 px-3 rounded-full text-xs font-medium ${kindFilter === "" ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"}`} data-testid="leads-kind-all">Tutti</button>
        <button onClick={() => setKindFilter("demo")} className={`h-9 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 ${kindFilter === "demo" ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"}`} data-testid="leads-kind-demo">
          <Calendar className="h-3 w-3" /> Demo
        </button>
        <button onClick={() => setKindFilter("signup")} className={`h-9 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 ${kindFilter === "signup" ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900" : "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"}`} data-testid="leads-kind-signup">
          <Building2 className="h-3 w-3" /> Attivazione
        </button>
      </div>

      {err && <div className="text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 rounded-lg">{err}</div>}

      {!loading && filtered.length === 0 && (
        <div className="card-soft p-10 text-center" data-testid="leads-empty">
          <div className="text-4xl mb-3 opacity-40">📭</div>
          <div className="font-medium text-zinc-700 dark:text-zinc-300">Nessuna richiesta {STATUS.find((s) => s.v === statusFilter)?.label.toLowerCase()}</div>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Le nuove richieste arriveranno qui.</p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((l) => (
          <LeadCard
            key={l.id}
            lead={l}
            expanded={expanded === l.id}
            onToggle={() => setExpanded(expanded === l.id ? null : l.id)}
            onUpdate={updateStatus}
          />
        ))}
      </div>
    </div>
  );
}

function LeadCard({ lead, expanded, onToggle, onUpdate }) {
  const [notes, setNotes] = useState(lead.admin_notes || "");
  const isDemo = lead.kind === "demo";
  const Icon = isDemo ? Calendar : Building2;
  const title = isDemo ? lead.name : lead.business_name;
  const sub = isDemo
    ? `${TYPE_LABELS[lead.business_type] || lead.business_type}`
    : `${TYPE_LABELS[lead.business_type] || lead.business_type} · ${lead.owner_name}`;
  const statusObj = STATUS.find((s) => s.v === lead.status) || STATUS[0];

  return (
    <div className="card-soft overflow-hidden" data-testid={`lead-card-${lead.id}`}>
      <button onClick={onToggle} className="w-full p-4 sm:p-5 flex items-start gap-3 sm:gap-4 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/40 transition">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${isDemo ? "bg-blue-100 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300" : "bg-violet-100 text-violet-600 dark:bg-violet-950/50 dark:text-violet-300"}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-zinc-900 dark:text-zinc-100 truncate">{title}</div>
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusObj.tone}`}>
              {statusObj.label}
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
              {isDemo ? "Demo" : "Attivazione"}
            </span>
          </div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 truncate">{sub}</div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {lead.email}</span>
            <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {lead.phone}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {formatWhen(lead.created_at)}</span>
          </div>
        </div>
        <div className="text-zinc-400">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-zinc-100 dark:border-zinc-800 p-4 sm:p-5 space-y-4 bg-zinc-50/50 dark:bg-zinc-800/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {isDemo ? (
              <>
                <Detail label="Nome">{lead.name}</Detail>
                <Detail label="Tipo attività">{TYPE_LABELS[lead.business_type] || lead.business_type}</Detail>
                <Detail label="Data preferita">{formatDate(lead.preferred_date) || "—"}</Detail>
                <Detail label="Fascia oraria">{SLOT_LABELS[lead.preferred_slot] || "Indifferente"}</Detail>
              </>
            ) : (
              <>
                <Detail label="Attività">{lead.business_name}</Detail>
                <Detail label="Tipo">{TYPE_LABELS[lead.business_type] || lead.business_type}</Detail>
                <Detail label="Titolare">{lead.owner_name}</Detail>
                <Detail label="Dimensione team">{lead.team_size || "—"}</Detail>
                <Detail label="P. IVA">{lead.vat_number || "—"}</Detail>
                <Detail label="Indirizzo" wide>
                  {lead.address ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {lead.address}</span> : "—"}
                </Detail>
              </>
            )}
            <Detail label="Email">
              <a href={`mailto:${lead.email}`} className="text-blue-600 dark:text-blue-400 hover:underline">{lead.email}</a>
            </Detail>
            <Detail label="Telefono">
              <a href={`tel:${lead.phone}`} className="text-blue-600 dark:text-blue-400 hover:underline">{lead.phone}</a>
            </Detail>
            <Detail label="Inviato il" wide>{formatWhen(lead.created_at)}</Detail>
          </div>

          {lead.notes && (
            <div className="bg-white dark:bg-zinc-900 rounded-xl p-3 border border-zinc-100 dark:border-zinc-800">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1">Note del richiedente</div>
              <div className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">{lead.notes}</div>
            </div>
          )}

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-1.5 flex items-center gap-1">
              <MessageSquare className="h-3 w-3" /> Note interne (admin)
            </div>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
              placeholder="Es. Contattato il 12/02, chiede sconto per partita IVA…"
              className="w-full px-3 py-2 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-zinc-100"
              data-testid={`lead-notes-${lead.id}`}
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {lead.status !== "contacted" && (
              <button onClick={() => onUpdate(lead.id, "contacted", notes)} className="h-9 px-3.5 rounded-full text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1.5" data-testid={`lead-action-contacted-${lead.id}`}>
                <Phone className="h-3.5 w-3.5" /> Segna contattato
              </button>
            )}
            {lead.status !== "approved" && (
              <button onClick={() => onUpdate(lead.id, "approved", notes)} className="h-9 px-3.5 rounded-full text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5" data-testid={`lead-action-approved-${lead.id}`}>
                <Check className="h-3.5 w-3.5" /> Approva
              </button>
            )}
            {lead.status !== "rejected" && (
              <button onClick={() => onUpdate(lead.id, "rejected", notes)} className="h-9 px-3.5 rounded-full text-xs font-medium bg-rose-100 hover:bg-rose-200 text-rose-700 dark:bg-rose-950/50 dark:hover:bg-rose-900/50 dark:text-rose-300 inline-flex items-center gap-1.5" data-testid={`lead-action-rejected-${lead.id}`}>
                <X className="h-3.5 w-3.5" /> Rifiuta
              </button>
            )}
            {lead.status !== "pending" && (
              <button onClick={() => onUpdate(lead.id, "pending", notes)} className="h-9 px-3.5 rounded-full text-xs font-medium bg-zinc-100 hover:bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:hover:bg-zinc-700 dark:text-zinc-300" data-testid={`lead-action-pending-${lead.id}`}>
                Riapri
              </button>
            )}
            {notes !== (lead.admin_notes || "") && (
              <button onClick={() => onUpdate(lead.id, lead.status, notes)} className="h-9 px-3.5 rounded-full text-xs font-medium bg-zinc-900 hover:bg-zinc-800 text-white dark:bg-zinc-100 dark:hover:bg-zinc-200 dark:text-zinc-900" data-testid={`lead-save-notes-${lead.id}`}>
                Salva note
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="text-sm text-zinc-800 dark:text-zinc-200 mt-0.5">{children}</div>
    </div>
  );
}
